import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import calcActivityTargets from '../utils/activityTargets';
import AdminAffiliatePanel from './AdminAffiliatePanel';

export default function MyProfile() {
  const {
    userData, setUserData,
    bonusTiers, setBonusTiers,
    onLogout, userEmail, setActiveTab,
  } = useAppContext();

  const autoTargets = calcActivityTargets(userData.unitsGoal);
  const [localUser, setLocalUser] = useState(userData);
  const [localTiers, setLocalTiers] = useState(bonusTiers);
  const settingsSaveTimer = useRef(null);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/api/stripe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create-portal', email: userEmail }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setPortalLoading(false);
    } catch {
      setPortalLoading(false);
    }
  };

  // Auto-save settings when localUser or localTiers change (debounced)
  useEffect(() => {
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    settingsSaveTimer.current = setTimeout(() => {
      setUserData(localUser);
      setBonusTiers(localTiers.sort((a, b) => a.unitsSold - b.unitsSold));
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 1500);
    }, 800);
    return () => { if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current); };
  }, [localUser, localTiers]);

  const save = () => {
    setUserData(localUser);
    setBonusTiers(localTiers.sort((a, b) => a.unitsSold - b.unitsSold));
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 1500);
  };

  const updateTier = (id, field, val) => {
    setLocalTiers(prev => prev.map(t => t.id === id ? { ...t, [field]: Number(val) } : t));
  };

  return (
    <div className="view">

      {/* Profile */}
      <div className="section-card">
        <h2 className="settings-h2">👤 My Profile</h2>
        <div className="settings-grid">
          <div className="setting-item">
            <label>Your Name</label>
            <input type="text" placeholder="e.g. John Smith"
              value={localUser.name}
              onChange={e => setLocalUser({...localUser, name: e.target.value})}/>
          </div>
          <div className="setting-item">
            <label>Dealership Name</label>
            <input type="text" placeholder="e.g. ABC Motors"
              value={localUser.dealership || ''}
              onChange={e => setLocalUser({...localUser, dealership: e.target.value})}/>
          </div>
        </div>
        <div className="settings-grid" style={{marginTop:'12px'}}>
          <div className="setting-item">
            <label>Your Role</label>
            <select value={localUser.role || 'Salesperson'}
              onChange={e => setLocalUser({...localUser, role: e.target.value})}>
              <option>Salesperson</option>
              <option>Sales Manager</option>
              <option>Finance Manager</option>
              <option>GSM</option>
              <option>GM</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pay Plan */}
      <div className="section-card">
        <h2 className="settings-h2">💰 Your Pay Plan</h2>

        {/* Goals row */}
        <div className="settings-grid">
          <div className="setting-item">
            <label>Monthly Unit Goal</label>
            <input type="number" value={localUser.unitsGoal}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, unitsGoal: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, unitsGoal: 0}); }}/>
          </div>
          <div className="setting-item">
            <label>Monthly Gross Goal ($)</label>
            <input type="number" value={localUser.grossGoal}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, grossGoal: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, grossGoal: 0}); }}/>
          </div>
        </div>

        {/* Commission Mode toggle */}
        <div className="settings-divider"><span>Commission %</span></div>
        <div className="comm-mode-wrap">
          <p className="comm-mode-desc">How does your dealership calculate your commission?</p>
          <div className="comm-mode-pills">
            <button
              className={`mode-pill ${localUser.commissionMode === 'split' ? 'mode-active' : ''}`}
              onClick={() => setLocalUser({...localUser, commissionMode: 'split'})}>
              ⚡ Split — Front & Back
            </button>
            <button
              className={`mode-pill ${localUser.commissionMode === 'combined' ? 'mode-active' : ''}`}
              onClick={() => setLocalUser({...localUser, commissionMode: 'combined'})}>
              📦 Combined — Total Gross
            </button>
          </div>
          <p className="comm-mode-hint">
            {localUser.commissionMode === 'split'
              ? 'Different % on front-end (Front Desk) and back-end (Finance) gross separately. E.g. 8% front, 15% back.'
              : 'One % applied to total gross (Front + Back). Spiffs are added directly to earnings, not commissionable.'}
          </p>
        </div>

        {localUser.commissionMode === 'split' ? (<>
          <div className="settings-divider"><span>Split Commission Rates</span></div>
          <div className="settings-grid">
            <div className="setting-item">
              <label>Front End %</label>
              <div className="pct-input-wrap">
                <input type="number" min="0" max="100" value={localUser.frontPct}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, frontPct: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, frontPct: 0}); }}/>
                <span className="pct-symbol">%</span>
              </div>
              <span className="field-hint">e.g. 8</span>
            </div>
            <div className="setting-item">
              <label>Back End %</label>
              <div className="pct-input-wrap">
                <input type="number" min="0" max="100" value={localUser.backPct}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, backPct: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, backPct: 0}); }}/>
                <span className="pct-symbol">%</span>
              </div>
              <span className="field-hint">e.g. 15</span>
            </div>
          </div>
        </>) : (<>
          <div className="settings-divider"><span>Combined Commission Rate</span></div>
          <div className="settings-grid-single">
            <div className="setting-item">
              <label>Combined % (Front + Back)</label>
              <div className="pct-input-wrap">
                <input type="number" min="0" max="100" value={localUser.bothPct}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, bothPct: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, bothPct: 0}); }}/>
                <span className="pct-symbol">%</span>
              </div>
              <span className="field-hint">e.g. 15 — applied to front + back gross only. Spiffs added directly.</span>
            </div>
          </div>
        </>)}

        {/* Unit Bonuses */}
        <div className="settings-divider"><span>Unit Bonus ($ per car sold)</span></div>
        <div className="settings-grid">
          <div className="setting-item">
            <label>🚗 New Car Bonus ($)</label>
            <input type="number" value={localUser.newUnitBonus}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, newUnitBonus: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, newUnitBonus: 0}); }}/>
            <span className="field-hint">Flat $ per new unit</span>
          </div>
          <div className="setting-item">
            <label>🔧 Used Car Bonus ($)</label>
            <input type="number" value={localUser.usedUnitBonus}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, usedUnitBonus: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, usedUnitBonus: 0}); }}/>
            <span className="field-hint">Flat $ per used unit</span>
          </div>
        </div>

        {/* Draw */}
        <div className="settings-divider"><span>Monthly Draw</span></div>
        <div className="settings-grid-single">
          <div className="setting-item">
            <label>Monthly Draw ($)</label>
            <input type="number" value={localUser.drawMonthly}
              onFocus={e => { if (e.target.value === '0') e.target.select(); }}
              onChange={e => setLocalUser({...localUser, drawMonthly: e.target.value === '' ? '' : +e.target.value})}
              onBlur={e => { if (e.target.value === '') setLocalUser({...localUser, drawMonthly: 0}); }}/>
            <span className="field-hint">Advance drawn against commissions</span>
          </div>
        </div>

        {/* Live preview */}
        <div className="pay-preview">
          <div className="pay-preview-title">📐 Pay Plan Preview</div>
          <div className="pay-preview-grid">
            {localUser.commissionMode === 'split' ? (<>
              <div className="pp-row"><span>Front End:</span><strong>{localUser.frontPct}%</strong></div>
              <div className="pp-row"><span>Back End:</span><strong>{localUser.backPct}%</strong></div>
            </>) : (<>
              <div className="pp-row"><span>Combined %:</span><strong>{localUser.bothPct}%</strong></div>
            </>)}
            <div className="pp-row"><span>New Unit Bonus:</span><strong>${localUser.newUnitBonus}</strong></div>
            <div className="pp-row"><span>Used Unit Bonus:</span><strong>${localUser.usedUnitBonus}</strong></div>
            <div className="pp-row"><span>Monthly Draw:</span><strong>${localUser.drawMonthly}</strong></div>
          </div>
        </div>
      </div>

      {/* Recommended Activity Targets */}
      <div className="section-card recommend-card">
        <h2 className="settings-h2">📊 Recommended Activity Targets</h2>
        <p className="recommend-desc">
          Based on your <strong>{localUser.unitsGoal}-unit goal</strong>, a 20% close rate, and the industry standard of
          <strong> 3 calls · 2 emails · 1 text</strong> per prospect:
        </p>
        <div className="recommend-grid">
          <div className="recommend-item">
            <Phone size={20} style={{color:'#00ff99'}}/>
            <div className="rec-val">{autoTargets.dailyCalls}/day</div>
            <div className="rec-lbl">Calls</div>
            <div className="rec-sub">{autoTargets.monthlyCalls}/month</div>
          </div>
          <div className="recommend-item">
            <Mail size={20} style={{color:'#ffc800'}}/>
            <div className="rec-val">{autoTargets.dailyEmails}/day</div>
            <div className="rec-lbl">Emails</div>
            <div className="rec-sub">{autoTargets.monthlyEmails}/month</div>
          </div>
          <div className="recommend-item">
            <MessageSquare size={20} style={{color:'#00e5ff'}}/>
            <div className="rec-val">{autoTargets.dailyTexts}/day</div>
            <div className="rec-lbl">Texts</div>
            <div className="rec-sub">{autoTargets.monthlyTexts}/month</div>
          </div>
        </div>
        <div className="recommend-formula">
          <strong>Formula:</strong> {localUser.unitsGoal} units ÷ 20% close = {autoTargets.monthlyProspects} prospects/month
          → {autoTargets.monthlyCalls} calls + {autoTargets.monthlyEmails} emails + {autoTargets.monthlyTexts} texts
        </div>
        <div className="auto-toggle">
          <label className="toggle-label">
            <input type="checkbox" checked={localUser.useAutoTargets}
              onChange={e => setLocalUser({...localUser, useAutoTargets: e.target.checked})}/>
            <span className="toggle-track"><span className="toggle-thumb"/></span>
            Auto-update targets when goal changes
          </label>
        </div>
      </div>

      {/* Manual Activity Targets */}
      <div className="section-card">
        <h2 className="settings-h2">🎯 Daily Activity Targets</h2>
        <p className="settings-note">{localUser.useAutoTargets ? 'Auto-calculated from your goal above.' : 'Set your own custom targets.'}</p>
        <div className="settings-grid">
          {[
            { label:'Daily Calls Goal',   key:'callsTarget'  },
            { label:'Daily Texts Goal',   key:'textsTarget'  },
            { label:'Daily Emails Goal',  key:'emailsTarget' },
          ].map(({ label, key }) => (
            <div key={key} className="setting-item">
              <label>{label}</label>
              <input type="number" value={localUser[key]}
                disabled={localUser.useAutoTargets}
                onChange={e => setLocalUser({...localUser, [key]: +e.target.value})}/>
            </div>
          ))}
        </div>
      </div>

      {/* Bonus Tiers */}
      <div className="section-card">
        <h2 className="settings-h2">🏆 Bonus Tiers</h2>
        <div className="tier-settings">
          {localTiers.map((tier, i) => (
            <div key={tier.id} className="tier-setting-row">
              <span className="tier-num">Tier {i+1}</span>
              <input type="number" placeholder="Units" value={tier.unitsSold}
                onChange={e => updateTier(tier.id, 'unitsSold', e.target.value)}/>
              <span className="tier-eq">units =</span>
              <input type="number" placeholder="$" value={tier.bonusAmount}
                onChange={e => updateTier(tier.id, 'bonusAmount', e.target.value)}/>
              <button className="btn-del" onClick={() => setLocalTiers(prev => prev.filter(t=>t.id!==tier.id))}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
          <button className="btn-add-tier" onClick={() => setLocalTiers(prev => [
            ...prev, { id: Date.now().toString(), unitsSold: 0, bonusAmount: 0 }
          ])}><Plus size={14}/> Add Tier</button>
        </div>
      </div>

      <button className="btn-save" onClick={save}>{settingsSaved ? '✅ Saved!' : 'Save All Settings'}</button>

      {/* Account */}
      <div className="section-card" style={{marginTop:'20px'}}>
        <h2 className="settings-h2">👤 Account</h2>
        <div className="account-info">
          <div className="account-row">
            <span className="account-label">Signed in as:</span>
            <span className="account-value">{userEmail}</span>
          </div>
          <div className="account-row" style={{marginTop:'8px'}}>
            <span className="account-label">Plan:</span>
            <button
              onClick={() => setActiveTab('plans')}
              style={{background:'none',border:'none',padding:0,color:'#00ff99',fontWeight:700,fontSize:'14px',cursor:'pointer',fontFamily:'inherit'}}
            >
              Manage Plan →
            </button>
          </div>
          <div className="account-row" style={{marginTop:'8px'}}>
            <span className="account-label">Help & Support:</span>
            <a href="/support" className="account-value" style={{color:'#00ff99',textDecoration:'none',fontWeight:600}}>Visit Support Page →</a>
          </div>
        </div>
        <button
          onClick={openBillingPortal}
          disabled={portalLoading}
          style={{
            width:'100%', marginTop:'16px',
            background:'rgba(0,255,136,.08)', border:'1px solid rgba(0,255,136,.25)',
            color:'#00ff99', borderRadius:'12px', padding:'13px',
            fontSize:'15px', fontWeight:700, cursor: portalLoading ? 'wait' : 'pointer',
            transition:'all .15s', opacity: portalLoading ? 0.7 : 1,
          }}
        >
          {portalLoading ? 'Opening billing portal…' : '💳 Manage Billing'}
        </button>
        <p style={{fontSize:'12px',color:'rgba(255,255,255,.35)',margin:'8px 0 12px',textAlign:'center',lineHeight:1.5}}>
          Cancel subscription, update payment method, or download invoices
        </p>
        <button className="btn-logout" onClick={onLogout}>
          Sign Out
        </button>
      </div>

      {/* Affiliate Management (admin only) */}
      <AdminAffiliatePanel userEmail={userEmail} />
    </div>
  );
}
